import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Home as HomeIcon, Users, Target, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage = () => {
  const initiatives = [
    {
      title: 'Parker House of Baton Rouge',
      description: 'Maternity residential homes providing safe shelter, prenatal care, and holistic support for expecting mothers in crisis.',
      icon: HomeIcon,
      image: 'https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/pnrq309j_E259CC80-8178-4D03-BDB6-C1A9EF07CE7C.png'
    },
    {
      title: 'Transitional Housing',
      description: 'Stable housing and family stabilization programs that empower mothers to build independent, thriving lives.',
      icon: Users,
      image: 'https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/jxnzfvqe_15011002-79D8-4A1D-9B24-14C620067361.png'
    },
    {
      title: 'Community Development',
      description: 'Legacy programs focused on generational restoration, education, and long-term community stewardship.',
      icon: Target,
      image: 'https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/hkl9nftg_5EEA9B22-C9CC-4667-B8FA-D283940F398E.png'
    }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Faith-Anchored Leadership',
      description: 'Rooted in covenant values and guided by unwavering faith in restoration.'
    },
    {
      icon: Shield,
      title: 'Dignity of Motherhood',
      description: 'Honoring the sacred role of mothers and protecting families in crisis.'
    },
    {
      icon: Users,
      title: 'Generational Responsibility',
      description: 'Building legacy that transcends generations through intentional stewardship.'
    },
    {
      icon: Target,
      title: 'Excellence & Accountability',
      description: 'Operating with integrity, transparency, and commitment to lasting impact.'
    }
  ];

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/at6zaw34_4FF7C568-1205-4E64-9EE8-F0634611DB7F.png"
            alt="Multigenerational families"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-emerald-900/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-fade-in">
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-6 leading-tight tracking-wide">
            Building Legacy.<br />
            Restoring Families.<br />
            Sheltering Hope.
          </h1>
          <p className="text-xl md:text-2xl text-ivory mb-12 font-light tracking-wide">
            A multi-generational foundation committed to faith, family, and futures.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/donate">
              <Button className="bg-gold hover:bg-gold-dark text-emerald-900 text-lg px-10 py-7 rounded-sm font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                Partner With Us
                <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-2 border-ivory text-ivory hover:bg-ivory hover:text-emerald-900 text-lg px-10 py-7 rounded-sm font-semibold transition-all duration-300">
                Our Mission
              </Button>
            </Link>
          </div>
        </div>

        {/* Decorative gold divider */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
      </section>

      {/* Who We Are */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl md:text-5xl text-emerald-900 mb-6">Who We Are</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
            <p className="text-lg md:text-xl text-navy leading-relaxed max-w-4xl mx-auto">
              The Parker Family Legacy Foundation is a faith-anchored, legacy-driven philanthropic organization 
              dedicated to generational restoration, maternal housing, family stabilization, and community stewardship. 
              Our mission is to uplift the human race by providing essential support and resources. As a Louisiana-based 
              non-profit, we are dedicated to <span className="font-semibold text-emerald-800">Empowering Lives & Inspiring Change</span> on a global scale.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mt-16">
            <div className="order-2 md:order-1">
              <p className="text-lg text-navy leading-relaxed mb-6">
                Founded on the principles of covenant values and generational vision, we believe in building 
                institutions that will outlive their founders. Our work is rooted in the dignity of motherhood, 
                the strength of family, and the power of faith to restore and transform lives.
              </p>
              <p className="text-lg text-navy leading-relaxed">
                Through our flagship Parker House initiative and expanding programs, we provide not just 
                shelter and support, but a pathway to lasting legacy for mothers, families, and communities.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <img
                src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/nrcoy49h_292F5F56-24E7-4B2C-AE72-78CF83C2F16E.png"
                alt="Parker House of Baton Rouge"
                className="rounded-sm shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Flagship Initiatives */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl md:text-5xl text-emerald-900 mb-6">Our Flagship Initiatives</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {initiatives.map((initiative, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-sm shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={initiative.image}
                    alt={initiative.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/90 via-emerald-900/40 to-transparent"></div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <initiative.icon className="h-10 w-10 text-gold mb-4" />
                    <h3 className="font-serif text-2xl mb-3">{initiative.title}</h3>
                    <p className="text-ivory/90 leading-relaxed">{initiative.description}</p>
                  </div>
                </div>
                
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-gold text-emerald-900 p-2 rounded-full">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 px-6 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-block mb-6">
              <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
              <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Our Values</h2>
              <div className="h-px w-16 bg-gold mx-auto"></div>
            </div>
            <p className="text-xl text-ivory max-w-3xl mx-auto">
              The principles that guide our mission and define our commitment to lasting impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center group hover:transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/10 border-2 border-gold mb-6 group-hover:bg-gold group-hover:scale-110 transition-all duration-300">
                  <value.icon className="h-10 w-10 text-gold group-hover:text-emerald-900 transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl text-gold mb-4">{value.title}</h3>
                <p className="text-ivory/90 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Vision */}
      <section className="py-24 px-6 bg-ivory">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block mb-8">
            <div className="h-px w-16 bg-gold mx-auto mb-4"></div>
            <h2 className="font-serif text-4xl md:text-5xl text-emerald-900 mb-6">Our Vision for Impact</h2>
            <div className="h-px w-16 bg-gold mx-auto"></div>
          </div>
          
          <p className="text-xl text-navy leading-relaxed mb-12 max-w-4xl mx-auto">
            We envision a future where every mother has access to safe housing, every family experiences 
            stability, and every community witnesses generational restoration. Though we are in our 
            foundation stage, we have already begun serving our community through essential support—providing 
            free clothes and essentials to families in need. Our expansion across states will multiply our 
            impact, touching thousands of lives while maintaining the dignity and excellence that define our foundation.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-4">2025</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">Foundation Year</div>
              <div className="text-navy">Beginning our legacy of service and restoration</div>
            </div>
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-4">10</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">States Expansion Goal</div>
              <div className="text-navy">Nationwide restoration vision</div>
            </div>
            <div className="bg-white p-8 rounded-sm shadow-md">
              <div className="text-5xl font-serif text-gold mb-4">100</div>
              <div className="text-lg font-semibold text-emerald-900 mb-2">Years Vision</div>
              <div className="text-navy">A foundation built to last</div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_faith-legacy/artifacts/hkl9nftg_5EEA9B22-C9CC-4667-B8FA-D283940F398E.png"
            alt="Supportive Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-900/85"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="font-serif text-4xl md:text-6xl mb-8">Join Us in Building Legacy</h2>
          <p className="text-xl md:text-2xl text-ivory mb-12 leading-relaxed">
            Your partnership creates lasting change. Together, we restore families, shelter hope, and build futures.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/donate">
              <Button className="bg-gold hover:bg-gold-dark text-emerald-900 text-lg px-12 py-7 rounded-sm font-semibold transition-all duration-300 hover:scale-105">
                Donate Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-900 text-lg px-12 py-7 rounded-sm font-semibold transition-all duration-300">
                Become a Partner
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
